import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Typography,
  Snackbar,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormLabel,
  Radio,
  Modal,
  Paper,
  Input,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { SettingsOutlined, SaveOutlined } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

import GoBack from 'js/containers/GoBack.js';

import {
  confirmDate,
  maskCEP,
  findIndexInArray,
  findValueInArray,
  trackEventMatomoVisit,
  trackEventMatomo,
} from 'js/library/utils/helpers.js';

import axios from 'axios';
import { getLastUserInfo } from 'js/library/utils/helpers';
import { getUserInfoBasic_v1 } from 'js/library/utils/API/getUserInfo_v1.js';
import { editUserInfoAction } from 'js/core/actions/editUserInfoActions.js';

import Profile_Avatar from 'styles/assets/avatar/Profile_Avatar.png';
import { avatar } from 'styles/avatar';
import { userUpdate } from 'js/library/utils/API/apiUserUpdate';
import { MaskedField } from 'js/containers/UserForm/MaskedField.js';
import { processOrder } from 'js/library/utils/API/processOrder';

const useStyles = makeStyles(() => ({
  specialOutlineOn: {
    borderColor: 'black !important',
    borderWidth: 1,
    '&:hover:not($disabled):not($error):not($focused):before': {
      borderWidth: 10,
    },
  },
  specialOutlineOff: {
    borderWidth: 0,
  },
  labelProps: {
    color: 'black !important',
    marginTop: '10px',
  },
}));

const EditUserInfo = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { success } = useSelector((state) => state.editUserInfoComponent);

  const initModel = getLastUserInfo();
  const [uId] = useState(initModel.uId);
  const thumb = initModel.thumbnail === undefined ? Profile_Avatar : avatar[initModel.thumbnail];

  const cellPhone = initModel.contactList?.find((c) => c.type === 'cellPhone')?.value || '';
  const cpf = initModel.documentList?.find((d) => d.type === 'cpf')?.value || null;
  const triiboId = initModel.triiboId;
  const triiboCode = initModel.triiboCode;
  const isAnonymous = triiboId.includes('@anonymous-triibo.com.br');

  // Estados principais
  const [aliasName, setAliasName] = useState(null);
  const [email, setEmail] = useState(null);
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState('0');
  const [address, setAddress] = useState(null);
  const [optin, setOptin] = useState(false);
  const [triiboCodeState, setTriiboCodeState] = useState(triiboCode);
  const [cellPhoneState] = useState(cellPhone);
  const [cpfState] = useState(cpf);

  const [newAliasName, setNewAliasName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newGender, setNewGender] = useState('0');
  const [newBirthDate, setNewBirthDate] = useState('');
  const [newFullAddress, setNewFullAddress] = useState(null);
  const [newAddressCEP, setNewAddressCEP] = useState('');
  const [newAddressNumber, setNewAddressNumber] = useState('');
  const [newAddressExtra, setNewAddressExtra] = useState('');
  const [newAddressType, setNewAddressType] = useState('residential');
  const [newOptin, setNewOptin] = useState(false);
  const [inviterId, setInviterId] = useState('');

  const [editUser, setEditUser] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [messageSnackbar, setMessageSnackbar] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [lastInfo, setLastInfo] = useState(true);
  const [userInfoModel, setUserInfoModel] = useState(null);

  // Redireciona se anônimo
  useEffect(() => {
    if (isAnonymous) {
      navigate('');
    }
  }, [isAnonymous, navigate]);

  // Carga inicial com getUserInfoBasic_v1
  useEffect(() => {
    document.title = 'Meus dados';
    trackEventMatomoVisit('MeusDados');

    const userInfo = {
      contactList: [{ type: 'cellPhone', value: cellPhone }],
    };

    if (!isAnonymous) {
      getUserInfoBasic_v1(['getUserInfo'], userInfo).then((model) => {
        const tempDate = model.birthDate ? new Date(model.birthDate) : null;
        const address =
          Array.isArray(model.addressList) && model.addressList.length > 0
            ? model.addressList[0]
            : null;

        const formattedDate =
          tempDate === null
            ? ''
            : `${tempDate.getDate().toString().padStart(2, '0')}/${(tempDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${tempDate.getFullYear()}`;

        const optin =
          Array.isArray(model.optInList) &&
          findIndexInArray(model.optInList, 'optInId', '-KzjMOHvzce-PUYpoWHU') !== null &&
          model.optInList[findIndexInArray(model.optInList, 'optInId', '-KzjMOHvzce-PUYpoWHU')]
            ?.accept === true;

        setUserInfoModel(model);
        setAliasName(model.aliasName);
        setEmail(findValueInArray(model.contactList, 'type', 'email')?.value || null);
        setTriiboCodeState(model.triiboCode || null);
        setBirthDate(formattedDate);
        setGender(String(model.gender));
        setAddress(address);
        setOptin(optin);

        // Campos editáveis
        setNewAliasName(model.aliasName);
        setNewEmail(findValueInArray(model.contactList, 'type', 'email')?.value || '');
        setNewBirthDate(formattedDate);
        setNewGender(String(model.gender));
        setNewFullAddress(address);
        setNewAddressExtra(address?.extra || '');
        setNewAddressCEP(address?.zipCode || '');
        setNewAddressNumber(address?.number || '');
        setNewAddressType(
          address?.type === undefined || address?.type === 'principal'
            ? 'residential'
            : address?.type
        );
        setNewOptin(optin);

        setLoaded(true);
      });
    }
  }, [cellPhone, isAnonymous]);

  const handleChange = useCallback((name, value) => {
    console.log('--------------', name, value);
    switch (name) {
      case 'newAddressCEP':
        const masked = maskCEP(value);
        setNewAddressCEP(masked);

        if (masked.length === 9) {
          axios.get(`https://viacep.com.br/ws/${masked}/json/`).then((result) => {
            if (result.data.erro !== undefined) {
              setOpenSnackbar(true);
              setMessageSnackbar('CEP não encontrado.');
              setNewAddressCEP('');
            } else {
              const fullAddress = {
                type: 'principal',
                googleAddr: `${result.data.logradouro} - ${result.data.bairro}, ${result.data.localidade} - ${result.data.uf}, ${result.data.cep}, Brasil`,
                latitude: 0,
                longitude: 0,
                streetAve: result.data.logradouro,
                number: null,
                state: result.data.uf,
                city: result.data.localidade,
                neighborhood: result.data.bairro,
                country: 'Brasil',
                zipCode: result.data.cep,
              };
              setNewFullAddress(fullAddress);
              setOpenSnackbar(false);
            }
          });
        } else if (masked.length < 9) {
          setNewFullAddress(null);
        }
        break;

      case 'newOptin':
        setNewOptin(value);
        break;

      case 'inviterId':
        setInviterId('T-' + value.replace(/\D/g, '').substr(0, 7));
        break;

      default:
        // Campos genéricos
        if (name === 'newAliasName') setNewAliasName(value);
        else if (name === 'newEmail') setNewEmail(value);
        else if (name === 'newGender') setNewGender(value);
        else if (name === 'newBirthDate') setNewBirthDate(value);
        else if (name === 'newAddressNumber') setNewAddressNumber(value);
        else if (name === 'newAddressExtra') setNewAddressExtra(value);
        else if (name === 'newAddressType') setNewAddressType(value);
        break;
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!newAddressCEP || newAddressCEP.length !== 9) {
      setOpenSnackbar(true);
      setMessageSnackbar('CEP inválido.');
    } else if (!confirmDate(newBirthDate)) {
      setOpenSnackbar(true);
      setMessageSnackbar('Data de nascimento inválida!');
    } else {
      dispatch(
        editUserInfoAction(
          dispatch,
          uId,
          newAliasName,
          newEmail,
          newGender,
          newBirthDate,
          newFullAddress,
          newAddressExtra,
          newAddressNumber,
          newAddressType,
          userInfoModel,
          newOptin
        )
      );
      setLoaded(false);
      setLastInfo(false);
      setEditUser(false);
    }
  }, [
    newAddressCEP,
    newBirthDate,
    dispatch,
    uId,
    newAliasName,
    newEmail,
    newGender,
    newFullAddress,
    newAddressExtra,
    newAddressNumber,
    newAddressType,
    userInfoModel,
    newOptin,
  ]);

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const openEdition = () => {
    if (editUser) {
      if (
        newAliasName === aliasName &&
        newEmail === email &&
        newBirthDate === birthDate &&
        newAddressCEP === address?.zipCode &&
        newAddressNumber === address?.number &&
        newAddressExtra === address?.extra &&
        newGender === gender
      ) {
        setEditUser(false);
      } else {
        setEditDialog(true);
      }
    } else {
      setEditUser(true);
    }
  };
  const inputInviterCode = () => {
    userUpdate(uId, triiboId);
    processOrder(
      uId,
      {
        itemId: 'invite-001',
        receiver: triiboId,
        requestDate: Date.now(),
        requestLat: 0,
        requestLon: 0,
        sender: inviterId,
        type: 'invite',
      },
      false
    );
    setInviteModal(false);
    setInviterId('');
  };

  const updateInfo = useCallback(() => {
    setAliasName(newAliasName);
    setBirthDate(newBirthDate);
    setEmail(newEmail);
    setAddress(newFullAddress);
    setGender(newGender);
    setOptin(newOptin);
    setLastInfo(true);
    setLoaded(true);
    setEditUser(false);
  }, [newAliasName, newBirthDate, newEmail, newFullAddress, newGender, newOptin]);

  // Atualização condicional com sucesso
  useEffect(() => {
    if (success && !lastInfo) {
      updateInfo();
    }
  }, [success, lastInfo, updateInfo]);

  return (
    <div>
      <GoBack />

      {!loaded ? (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ textAlign: 'right' }}>
            {editUser && (
              <IconButton
                onClick={() => trackEventMatomo('Meus dados', 'tocar', 'botao', 'Salvar')}
                type="submit"
              >
                <Tooltip title={t('profile.save')}>
                  <SaveOutlined color="primary" style={{ cursor: 'pointer' }} />
                </Tooltip>
              </IconButton>
            )}

            <IconButton
              disabled={!loaded}
              onClick={() => {
                openEdition();
                trackEventMatomo('Meus dados', 'tocar', 'botao', 'Editar');
              }}
            >
              <Tooltip title={editUser ? t('profile.cancel') : t('profile.edit')}>
                <SettingsOutlined style={{ color: editUser ? 'red' : '#6D3195' }} />
              </Tooltip>
            </IconButton>
          </div>
          <Grid container className="meusDados__container">
            <Grid size={{ sm: 6, xs: 12 }}>
              <div style={{ textAlign: 'center' }}>
                <img width="65%" alt="profileThumb" src={thumb} />
              </div>
            </Grid>

            <Grid size={{ sm: 6, xs: 12 }}>
              <Typography variant="h6" color="secondary" fontWeight="bold">
                {aliasName}
              </Typography>

              {triiboCodeState && (
                <>
                  <Typography variant="caption" color="secondary">
                    {t('profile.my_triibo_code')}
                  </Typography>
                  <Typography variant="subtitle2" color="secondary" fontWeight="bold">
                    {triiboCodeState}
                  </Typography>
                </>
              )}

              {cellPhoneState && (
                <>
                  <Typography variant="caption" color="secondary">
                    {t('profile.phone_number')}
                  </Typography>
                  <Typography variant="subtitle2" color="secondary" fontWeight="bold">
                    ({cellPhoneState.substr(3, 2)}) {cellPhoneState.substr(5, 5)}-
                    {cellPhoneState.substr(10, 4)}
                  </Typography>
                </>
              )}

              {cpfState && (
                <>
                  <Typography variant="caption" color="secondary">
                    {t('profile.cpf')}
                  </Typography>
                  <Typography variant="subtitle2" color="secondary" fontWeight="bold">
                    {cpfState.substr(0, 3)}.{cpfState.substr(3, 3)}.{cpfState.substr(6, 3)}-
                    {cpfState.substr(9, 2)}
                  </Typography>
                </>
              )}

              <div style={{ padding: '15px 0 25px' }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setInviteModal(true);
                    trackEventMatomo('Meus dados', 'tocar', 'botao', 'Fui convidado');
                  }}
                >
                  <span style={{ fontWeight: 'bold', color: 'white', padding: '0 15px' }}>
                    {t('profile.was_invited')}
                  </span>
                </Button>
              </div>
            </Grid>
          </Grid>
          <TextField
            required
            id="name"
            label={t('profile.nickname')}
            variant="outlined"
            fullWidth
            disabled={!editUser}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            value={editUser ? newAliasName : aliasName}
            onChange={(e) => handleChange('newAliasName', e.target.value)}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <TextField
            id="email"
            label={t('profile.contact_mail')}
            variant="outlined"
            fullWidth
            disabled={!editUser}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            value={editUser ? newEmail : email}
            onChange={(e) => handleChange('newEmail', e.target.value)}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <div style={{ marginTop: 15, marginBottom: 15, paddingLeft: 15 }}>
            <FormLabel>{t('profile.gender')}</FormLabel>
          </div>
          <div style={{ textAlign: 'left' }}>
            <Radio
              disabled={!editUser}
              checked={(editUser ? newGender : gender) === '0'}
              onChange={(e) => handleChange('newGender', e.target.value)}
              value="0"
            />
            {t('profile.male')}

            <Radio
              disabled={!editUser}
              checked={(editUser ? newGender : gender) === '1'}
              onChange={(e) => handleChange('newGender', e.target.value)}
              value="1"
            />
            {t('profile.female')}
          </div>
          <MaskedField
            label={t('profile.birthday')}
            mask="99/99/9999"
            name="birthDate"
            id="birthDate"
            required
            disabled={!editUser}
            value={editUser ? newBirthDate : birthDate}
            onChange={(e) => setNewBirthDate(e.target.value)}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <MaskedField
            label={t('profile.zip')}
            mask="99999-999"
            id="newAddressCEP"
            name="newAddressCEP"
            variant="outlined"
            required
            fullWidth
            disabled={!editUser}
            value={editUser ? newAddressCEP : address?.zipCode || ''}
            onChange={(e) => setNewAddressCEP(e.target.value)}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />

          <TextField
            id="Address"
            label={t('profile.address')}
            variant="outlined"
            disabled
            fullWidth
            value={
              editUser
                ? newFullAddress?.streetAve && newFullAddress?.neighborhood
                  ? `${newFullAddress.streetAve} - ${newFullAddress.neighborhood}`
                  : ''
                : address?.streetAve && address?.neighborhood
                ? `${address.streetAve} - ${address.neighborhood}`
                : ''
            }
            InputProps={{
              classes: { notchedOutline: classes.specialOutlineOff },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <TextField
            id="cidade"
            label={t('profile.city')}
            variant="outlined"
            disabled
            fullWidth
            value={
              editUser
                ? newFullAddress?.city && newFullAddress?.state
                  ? `${newFullAddress.city} - ${newFullAddress.state}`
                  : ''
                : address?.city && address?.state
                ? `${address.city} - ${address.state}`
                : ''
            }
            InputProps={{
              classes: { notchedOutline: classes.specialOutlineOff },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <TextField
            id="number"
            required
            label={t('profile.number')}
            variant="outlined"
            fullWidth
            disabled={!editUser}
            value={editUser ? newAddressNumber : address?.number || ''}
            onChange={(e) => handleChange('newAddressNumber', e.target.value)}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <TextField
            id="complemento"
            label={t('profile.complement')}
            variant="outlined"
            fullWidth
            disabled={!editUser}
            value={editUser ? newAddressExtra : address?.extra || ''}
            onChange={(e) => handleChange('newAddressExtra', e.target.value)}
            InputProps={{
              classes: {
                notchedOutline: editUser ? classes.specialOutlineOn : classes.specialOutlineOff,
              },
              className: classes.labelProps,
            }}
            InputLabelProps={{ className: classes.labelProps }}
          />
          <FormControlLabel
            id="optin"
            control={
              <Checkbox
                disabled={!editUser}
                checked={editUser ? newOptin : optin}
                onChange={(e) => handleChange('newOptin', e.target.checked)}
              />
            }
            label={t('profile.accept_to_receive_communications')}
          />
          <FormControl required fullWidth style={{ marginTop: 10 }}>
            <InputLabel id="type" style={{ color: 'black' }}>
              {t('profile.address_type')}
            </InputLabel>
            <Select
              disabled={!editUser}
              labelId="type"
              id="type"
              variant="outlined"
              value={
                editUser
                  ? newAddressType
                  : address?.type === 'principal'
                  ? 'residential'
                  : address?.type
              }
              onChange={(e) => handleChange('newAddressType', e.target.value)}
            >
              <MenuItem value="residential">Residencial</MenuItem>
              <MenuItem value="comercial">Comercial</MenuItem>
            </Select>
          </FormControl>
        </form>
      )}

      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>{t('profile.cancel_editing_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('profile.cancel_editing_warning')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} color="primary">
            {t('profile.continue_editing')}
          </Button>
          <Button
            onClick={() => {
              setEditDialog(false);
              setEditUser(false);
            }}
            color="primary"
            autoFocus
          >
            {t('profile.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        ContentProps={{ 'aria-describedby': 'message-id' }}
        message={<span id="message-id">{messageSnackbar}</span>}
      />

      <Modal open={inviteModal} onClose={() => setInviteModal(false)}>
        <Paper
          style={{
            position: 'absolute',
            padding: '20px',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <FormLabel>{t('profile.enter_the_code_of_who_invited_you')}</FormLabel>
          <Input
            fullWidth
            type="tel"
            value={inviterId}
            onChange={(e) => handleChange('inviterId', e.target.value)}
            id="triibo-code"
          />
          <Button
            color="primary"
            disabled={inviterId.trim().length < 9}
            variant="contained"
            onClick={inputInviterCode}
            style={{ marginTop: 20 }}
          >
            <span style={{ fontWeight: 'bold', padding: '0px 15px' }}>{t('profile.confirm')}</span>
          </Button>
        </Paper>
      </Modal>
    </div>
  );
};

export default EditUserInfo;

